import React, { useState } from "react";
import {
  Box,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  Avatar,
  Stack,
  Menu,
  MenuItem,
  Divider,
  Chip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import Logout from "@mui/icons-material/Logout";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { useAuth } from "../hooks/useAuth";
import { signOut } from "firebase/auth";
import { auth } from "../firebase/config";

export default function SidebarLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { userDoc } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleCloseMenu();
    try {
      await signOut(auth);
      localStorage.removeItem("activeUserRole");
      navigate("/login");
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    }
  };

  const userName: string = userDoc?.name || userDoc?.displayName || userDoc?.email?.split("@")[0] || "Administrador";
  const userEmail: string = userDoc?.email || "admin@nextconnection.com";
  const avatarLetter = userName.charAt(0).toUpperCase();

  // Dynamic Section Title based on path
  const getSectionTitle = () => {
    const p = location.pathname;
    if (p.includes("web-credits")) return "Gestión de Créditos & Estudios";
    if (p.includes("web-appointments")) return "Agenda de Citas & Asesorías";
    if (p.includes("web-allies")) return "Aliados Estratégicos";
    if (p.includes("web-users")) return "Usuarios & Clientes del Portal";
    if (p.includes("web-bank-forms")) return "Matrices & Bancos";
    if (p.includes("web-categories")) return "Líneas & Categorías";
    if (p.includes("web-quotes")) return "Cotizaciones & Solicitudes";
    return "Panel General";
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f8fafc" }}>
      {/* 🔹 Sidebar (Fixed on Desktop, Drawer on Mobile) */}
      <Sidebar
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
        userDoc={userDoc}
        onAvatarClick={handleAvatarClick}
      />

      {/* 🔹 Main Content Area */}
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          bgcolor: "#f8fafc",
        }}
      >
        {/* 🔹 Executive Top Navigation Header */}
        <Box
          component="header"
          sx={{
            height: 64,
            bgcolor: "#ffffff",
            borderBottom: "1px solid #e2e8f0",
            px: { xs: 2, sm: 3, md: 4 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {isMobile && (
              <IconButton onClick={handleDrawerToggle} edge="start" size="small" sx={{ mr: 0.5 }}>
                <MenuIcon />
              </IconButton>
            )}
            <Box>
              <Typography variant="subtitle1" fontWeight={800} color="#0f172a" sx={{ lineHeight: 1.2 }}>
                {getSectionTitle()}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "none", sm: "block" } }}>
                Next Connection Group • Panel de Administración
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={2}>
            {/* Status Pill */}
            <Chip
              icon={<ShieldOutlinedIcon sx={{ fontSize: "14px !important", color: "#059669 !important" }} />}
              label="Ecosistema Activo"
              size="small"
              sx={{
                bgcolor: "#ecfdf5",
                color: "#065f46",
                fontWeight: 700,
                fontSize: "0.72rem",
                border: "1px solid #a7f3d0",
                display: { xs: "none", sm: "flex" },
              }}
            />

            {/* User Profile Capsule */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.2}
              onClick={handleAvatarClick}
              sx={{
                cursor: "pointer",
                p: 0.5,
                pr: 1.5,
                borderRadius: 3,
                border: "1px solid #e2e8f0",
                bgcolor: "#ffffff",
                transition: "all 0.15s ease",
                "&:hover": { bgcolor: "#f8fafc", borderColor: "#cbd5e1" },
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: "#0f172a",
                  fontSize: "0.8rem",
                  fontWeight: 800,
                }}
              >
                {avatarLetter}
              </Avatar>
              <Box sx={{ display: { xs: "none", md: "block" }, textAlign: "left" }}>
                <Typography variant="caption" fontWeight={800} color="#0f172a" display="block" sx={{ lineHeight: 1.1 }}>
                  {userName}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: "0.65rem", lineHeight: 1.1 }}>
                  Admin
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </Box>

        {/* 🔹 Dynamic Body View */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 2.5, md: 3.5 },
            minWidth: 0,
            boxSizing: "border-box",
          }}
        >
          <Outlet />
        </Box>
      </Box>

      {/* 🔹 User Profile Floating Logout Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 8px 20px rgba(15,23,42,0.08))",
            mt: 1.5,
            borderRadius: 3,
            minWidth: 220,
            p: 1,
            border: "1px solid #e2e8f0",
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box sx={{ px: 1.5, py: 1 }}>
          <Typography variant="subtitle2" fontWeight={800} color="#0f172a">
            {userName}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" noWrap>
            {userEmail}
          </Typography>
        </Box>
        <Divider sx={{ my: 1 }} />
        <MenuItem
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            color: "#ef4444",
            fontWeight: 700,
            fontSize: "0.82rem",
            gap: 1.2,
            py: 1,
            "&:hover": {
              bgcolor: "#fef2f2",
            },
          }}
        >
          <Logout fontSize="small" />
          Cerrar Sesión
        </MenuItem>
      </Menu>
    </Box>
  );
}
